/* eslint-disable @next/next/no-img-element */
import Link from 'next/link'
import * as React from 'react'

import { SignUpForm } from '@/components/forms/auth/sign-up-form'

import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

export interface NonFsdsSignUpSectionProps {
  heroTitle: string
  heroDescription: string
  formTitle: string
  formSubtitle: string
  logoUrl: string
  signInText: string
  brandHref?: string
  signInUrl?: string
  gradientFrom: string
  gradientTo: string
}

export default function NonFsdsSignUpSection({
  heroTitle = 'Start Your\nJourney',
  heroDescription = 'Create an account to gain access to world-class curriculum and kickstart your learning experience today.',
  formTitle = 'Create an Account',
  formSubtitle = 'Please enter your details below to sign up.',
  logoUrl = NON_FSDS_DEFAULT_LOGO_URL,
  signInText = 'Log in',
  brandHref = TEMPLATE_DEFAULT_URLS.home,
  signInUrl = TEMPLATE_DEFAULT_URLS.signIn,
  gradientFrom = '#1e3a8a',
  gradientTo = '#1d4ed8',
}: NonFsdsSignUpSectionProps) {
  const heroLines = heroTitle.split('\n')

  return (
    <div className="min-h-svh flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row-reverse w-full max-w-6xl bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] overflow-hidden">
        <div
          className="hidden lg:flex lg:w-5/12 p-12 flex-col justify-between relative overflow-hidden text-white"
          style={{ background: `linear-gradient(to bottom left, ${gradientFrom}, ${gradientTo})` }}
        >
          <div className="absolute top-0 right-0 w-full h-full opacity-30 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-400 via-transparent to-transparent" />
          <div className="absolute -bottom-32 -left-32 w-[30rem] h-[30rem] rounded-full bg-white/5 blur-3xl" />

          <div className="relative z-10 flex justify-end">
            <Link href={brandHref} className="inline-block transition-transform hover:scale-105 active:scale-95 duration-200">
              <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
            </Link>
          </div>

          <div className="relative z-10 mt-auto text-right">
            <h2 className="text-4xl font-bold tracking-tight mb-4 text-white leading-tight">
              {heroLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < heroLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h2>
            <p className="text-blue-100/90 text-lg ml-auto max-w-[22rem] leading-relaxed">{heroDescription}</p>
          </div>
        </div>

        <div className="w-full lg:w-7/12 p-8 sm:p-12 lg:px-16 lg:py-14 flex flex-col justify-center bg-white relative z-10">
          <div className="mx-auto w-full max-w-md">
            <div className="lg:hidden mb-10 flex justify-center">
              <Link href={brandHref} className="inline-block">
                <img src={logoUrl} alt="Logo" className="h-10 w-auto" />
              </Link>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-2">{formTitle}</h3>
            <p className="text-slate-500 mb-8 text-sm">{formSubtitle}</p>

            <SignUpForm buttonClassName="w-full bg-blue-600 text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:bg-blue-700 hover:shadow-blue-600/40 focus-visible:ring-blue-600 rounded-xl" />

            <div className="mt-8 text-center text-sm text-slate-500">
              Already have an account?{' '}
              <Link
                href={signInUrl}
                className="font-semibold text-blue-600 transition-colors hover:text-blue-700 hover:underline underline-offset-4"
              >
                {signInText}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
